import type { Fetcher, SearchRequest, SearchResponse } from './api.types'
import { ApiError } from './api.types'

const DEFAULT_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
}

/**
 * Creates a standard fetcher function for a given base URL.
 * Use this helper for simple cases where you just need to specify the API base URL.
 *
 * @param baseUrl - Base URL for the API (e.g., '/api', 'https://api.example.com')
 * @returns A fetcher function compatible with LookupField
 *
 * @example
 * // Simple usage
 * const fetcher = createFetcher('/api')
 * <LookupField entity="customers" fetcher={fetcher} />
 *
 * @example
 * // With authentication - wrap createFetcher or create custom fetcher
 * const fetcher = createFetcher('/api', {
 *   headers: { Authorization: `Bearer ${token}` }
 * })
 */
export function createFetcher(
  baseUrl: string,
  options?: { headers?: Record<string, string> }
): Fetcher {
  const headers = { ...DEFAULT_HEADERS, ...options?.headers }

  return async <TRecord>(
    entity: string,
    request: SearchRequest
  ): Promise<SearchResponse<TRecord>> => {
    const url = `${baseUrl}/${entity}/search`

    // Ensure 'Id' is always included in fields (FR20) - PascalCase standard
    const fields = request.fields.includes('Id') ? request.fields : ['Id', ...request.fields]

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...request, fields }),
    })

    if (!response.ok) {
      throw new ApiError(
        `API Error: ${response.status} ${response.statusText}`,
        response.status,
        response.statusText
      )
    }

    return response.json()
  }
}
